import React, { Suspense, useState, useEffect } from 'react';
import TourGuide from 'src/components/Banners/TourGuide';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import { useTranslation } from '@lt/localization';
import EmptyBlock from 'src/routes/Hotel/components/EmptyBlock';
import { useInView } from 'react-intersection-observer';
import { useForceRender } from 'src/hooks/useForceRender';
import HotelMapLoader from 'src/components/YandexMap/components/HotelMapLoader';
import HotelFAQ from 'src/components/HotelFAQ';
import Footer from 'src/components/Footer';
import HeaderContainer from '../Header';
import ReviewsDesktop from '../../components/Reviews/ReviewsDesktop';
import HotelInfo from '../HotelInfo';
import SearcherContainer from '../SearcherContainer';
import { COMPONENT_ID } from '../../constants';
import FacilitiesContainer from '../FacilitiesContainer';
import Container from '../../components/Container';
import Gallery from '../Gallery';
import RatingBadge from '../RatingBadge';
import TopMenuContainer from '../TopMenu/TopMenuContainer';
import HintFactory from '../../../../components/Hint/HintFactory';
import OfferExtras from '../../components/TooltipContent/OfferExtras';
import RoomFacilitiesHintFactory from '../HintFactory';
import ExtraItemsContainer from '../ExtraItemsContainer';
import Paper from '../../components/Paper';
import FortunaJumbotronContainer from '../FortunaJumbotronContainer';
import ExperienceContainer from '../ExperienceContainer';
import IncludedServicesHint from './IncludedServicesHint';
import ReviewsOldDesktop from '../../components/Reviews/OldReviewsComponents/ReviewsDesktop';
import HotelMap from '../HotelMap';

interface LayoutDesktopProps {
  isRoulette: boolean;
  isHotelOnly: boolean;
  isNew?: boolean;
  showHotelReviews: () => void;
  setVariantReviews: (variant: 'new' | 'old') => void;
  isShowHotelFAQ: boolean;
  isWhitelabel: boolean;
}

const LayoutDesktop = (props: LayoutDesktopProps) => {
  const {
    isRoulette,
    isHotelOnly,
    isNew = 'old',
    showHotelReviews,
    setVariantReviews,
    isShowHotelFAQ,
    isWhitelabel,
  } = props;

  const { t } = useTranslation();

  const [isSSR, setIsSSR] = useState(true);

  useEffect(() => {
    setIsSSR(false);
  }, []);

  const { countForceRender } = useForceRender();

  useEffect(() => {
    setVariantReviews('new');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      showHotelReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <div className="hotel-desktop">
      <HintFactory events attribute="data-text-hint" isMobile={false} />
      <IncludedServicesHint isMobile={false} />
      <HintFactory
        isMobile={false}
        autoPosition
        attribute="data-offer-extras"
        className="react-hint"
        events
        onRenderContent={(target, content) =>
          content && (
            <div className="react-hint__content">
              <OfferExtras extras={JSON.parse(content)} />
            </div>
          )
        }
      />
      <RoomFacilitiesHintFactory />
      <Container marginBottom={0} marginTop={16}>
        <HeaderContainer isHotelOnly={isHotelOnly} />
      </Container>
      <Container id={COMPONENT_ID.GALLERY} marginBottom={0}>
        {isRoulette ? (
          <FortunaJumbotronContainer />
        ) : (
          <>
            <RatingBadge isMobile={false} />
            <Gallery />
          </>
        )}
      </Container>
      <TopMenuContainer isHotelOnly={isHotelOnly} />
      <Container id={COMPONENT_ID.SEARCHER}>
        <SearcherContainer />
      </Container>
      {!isHotelOnly && !isWhitelabel && (
        <Container>
          <TourGuide />
        </Container>
      )}
      <ExperienceContainer
        fullWidth
        id={COMPONENT_ID.EXPERIENCE}
        marginBottom={0}
      />

      <Container id={COMPONENT_ID.HOTEL_FACTS} white>
        {isRoulette ? (
          <Paper padding={20}>
            <ExtraItemsContainer />
          </Paper>
        ) : (
          <HotelInfo />
        )}
      </Container>

      <Container id={COMPONENT_ID.MAP}>
        {isSSR ? null : (
          <ErrorBoundary
            fallback={
              <EmptyBlock
                isMobile
                title={t('map.static.message.title')}
                description={t('map.static.message.description')}
              />
            }
            showDialog={false}
          >
            <Suspense fallback={<HotelMapLoader />}>
              <HotelMap />
            </Suspense>
          </ErrorBoundary>
        )}
      </Container>

      {!isRoulette && (
        <>
          <Container id={COMPONENT_ID.FACILITIES} white>
            <FacilitiesContainer />
          </Container>
          <Container id={COMPONENT_ID.REVIEWS} containerRef={ref}>
            {isNew ? <ReviewsDesktop /> : <ReviewsOldDesktop />}
          </Container>

          {isShowHotelFAQ && (
            <Container>
              <HotelFAQ />
            </Container>
          )}
        </>
      )}
      {!isWhitelabel && <Footer key={countForceRender} />}
    </div>
  );
};

export default LayoutDesktop;
